import {Menu} from '@mantine/core';
import React from 'react';
import {useReactFlow} from 'reactflow';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

export interface AnalysisFlowchartContextMenuPosition {
  x: number;
  y: number;
}

interface AnalysisFlowchartContextMenuProps {
  /** Viewport coordinates (e.g. `clientX`/`clientY` from the triggering `contextmenu` event). */
  position: AnalysisFlowchartContextMenuPosition;
  onClose: () => void;
}

/**
 * Context menu shown when right-clicking the flowchart's background canvas.
 *
 * Positioned using a zero-size, fixed-position `Menu.Target`, anchored at `position`. `reactflow`'s
 * `onPaneContextMenu` only fires when the click lands directly on the pane, so this menu never shows for
 * right-clicks on nodes or edges.
 */
export default function AnalysisFlowchartContextMenu({
  position,
  onClose,
}: AnalysisFlowchartContextMenuProps): JSX.Element | null {
  const {getActionInfo, setSelectedNodeIds} = useAnalysisFlowchartContext();
  const {getNodes} = useReactFlow();

  const pasteAction = getActionInfo(AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD);
  // Hide the menu if "Copy" is not enabled, because otherwise "Select all" is marginally useful
  const canCopy = getActionInfo(AnalysisFlowchartAction.COPY_TO_CLIPBOARD).enabled;

  if (!pasteAction.enabled && !canCopy) {
    return null;
  }

  return (
    <Menu
      opened={true}
      onClose={onClose}
      withinPortal={true}
      withArrow={false}
      position="bottom-start"
      shadow="md"
    >
      <Menu.Target>
        <div style={{position: 'fixed', left: position.x, top: position.y}} />
      </Menu.Target>
      <Menu.Dropdown>
        {pasteAction.enabled && (
          <Menu.Item icon={<BenchlingIcon icon="paste" />} onClick={() => pasteAction.doAction()}>
            Paste from clipboard
          </Menu.Item>
        )}
        <Menu.Item
          icon={<BenchlingIcon icon="square-check-outline" />}
          onClick={() => {
            setSelectedNodeIds(new Set(getNodes().map((node) => node.id)));
          }}
        >
          Select all
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
